import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';

export type ConsentOptions = {
  necessary: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
};

declare global {
  interface Window {
    gtag: (command: string, action: string, params: object) => void;
  }
}

type CookieContextValue = {
  showBanner: boolean;
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
  onClickAccept: () => void;
  onClickDecline: () => void;
};

const CookieContext = createContext<CookieContextValue | undefined>(undefined);

type CookieProviderProps = {
  children: ReactNode;
  gtmId: string;
};

export function CookieProvider({ children, gtmId }: CookieProviderProps) {
  const [showBanner, setShowBanner] = useState(localStorage.getItem('consentMode') === null);

  const hideBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const setConsent = useCallback((consent: ConsentOptions) => {
    const consentMode = {
      functionality_storage: consent.necessary ? 'granted' : 'denied',
      security_storage: consent.necessary ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      personalization: consent.preferences ? 'granted' : 'denied',
    };

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', consentMode);
    }

    localStorage.setItem('consentMode', JSON.stringify(consentMode));
  }, []);

  const onClickAccept = useCallback(() => {
    setConsent({
      necessary: true,
      analytics: true,
      preferences: true,
      marketing: true,
    });
    hideBanner();
    TagManager.initialize({ gtmId: gtmId ?? '' });
  }, [setConsent, hideBanner, gtmId]);

  const onClickDecline = useCallback(() => {
    setConsent({
      necessary: false,
      analytics: false,
      preferences: false,
      marketing: false,
    });
    hideBanner();
  }, [setConsent, hideBanner]);

  const value = useMemo(() => ({
    showBanner,
    setShowBanner,
    onClickAccept,
    onClickDecline,
  }), [showBanner, onClickAccept, onClickDecline]);

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookie() {
  const context = useContext(CookieContext);

  if (!context) {
    throw new Error('useCookie must be used within a CookieProvider');
  }

  return context;
}
